import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { trainingProgramsService, programEnrollmentsService, programMaterialsService, programFeedbackService } from '../../../services/trainingExchangeService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const SUB_TABS = [
  { id: 'programs', label: 'Programs', icon: 'GraduationCap' },
  { id: 'enrollments', label: 'Enrollments', icon: 'Users' },
  { id: 'materials', label: 'Materials', icon: 'FileText' },
  { id: 'feedback', label: 'Feedback', icon: 'Star' },
];

const STATUS_COLORS = {
  approved: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-slate-100 text-slate-600',
  enrolled: 'bg-blue-100 text-blue-700',
  attended: 'bg-emerald-100 text-emerald-700',
  absent: 'bg-red-100 text-red-700',
};

const FORMAT_ICONS = { online: 'Monitor', 'in-person': 'MapPin', hybrid: 'Layers' };

const AddProgramModal = ({ partners, onClose, onSuccess }) => {
  const [form, setForm] = useState({ program_name: '', description: '', delivering_partner_id: '', category: 'technical', level: 'intermediate', format: 'online', duration: '', max_seats: 20, learning_objectives: '', prerequisites: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = (e) => setForm(p => ({ ...p, [e?.target?.name]: e?.target?.value }));
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.program_name?.trim()) { setError('Program name is required.'); return; }
    setSaving(true); setError(null);
    try { await trainingProgramsService?.create({ ...form, program_status: 'pending' }); onSuccess(); }
    catch (err) { setError(err?.message || 'Failed to create program.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-purple-50 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Icon name="Plus" size={18} className="text-white" />
            </div>
            <h2 className="text-sm font-bold text-slate-900">Submit Training Program</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14 overflow-y-auto flex-1">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Program Name *</label><input name="program_name" value={form?.program_name} onChange={handleChange} placeholder="e.g. Cisco Networking Fundamentals" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Delivering Partner</label>
            <select name="delivering_partner_id" value={form?.delivering_partner_id} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 bg-white">
              <option value="">Select partner...</option>
              {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
            </select>
          </div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Description</label><textarea name="description" value={form?.description} onChange={handleChange} rows={2} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 resize-none" /></div>
          <div className="grid grid-cols-3 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Category</label>
              <select name="category" value={form?.category} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                {['technical', 'sales', 'general', 'compliance', 'product'].map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Level</label>
              <select name="level" value={form?.level} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                {['beginner', 'intermediate', 'advanced'].map(l => <option key={l} value={l}>{l?.charAt(0)?.toUpperCase() + l?.slice(1)}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Format</label>
              <select name="format" value={form?.format} onChange={handleChange} className="w-full px-10 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
                {['online', 'in-person', 'hybrid'].map(f => <option key={f} value={f}>{f?.charAt(0)?.toUpperCase() + f?.slice(1)}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Duration</label><input name="duration" value={form?.duration} onChange={handleChange} placeholder="e.g. 2 days" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Max Seats</label><input name="max_seats" type="number" value={form?.max_seats} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" /></div>
          </div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Learning Objectives</label><textarea name="learning_objectives" value={form?.learning_objectives} onChange={handleChange} rows={2} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 resize-none" /></div>
          <div className="flex gap-12 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-16 py-10 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-16 py-10 text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl hover:opacity-90 disabled:opacity-60">
              {saving ? 'Submitting...' : 'Submit Program'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EnrollModal = ({ program, onClose, onSuccess }) => {
  const [form, setForm] = useState({ member_name: '', member_email: '', member_role: '', member_department: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.member_name?.trim()) { setError('Member name is required.'); return; }
    setSaving(true); setError(null);
    try { await programEnrollmentsService?.create({ ...form, program_id: program?.id, attendance_status: 'enrolled' }); onSuccess(); }
    catch (err) { setError(err?.message || 'Failed to enroll.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Icon name="UserPlus" size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">Enroll Member</h2>
              <p className="text-xs text-slate-500 truncate max-w-[200px]">{program?.program_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Member Name *</label><input value={form?.member_name} onChange={e => setForm(p => ({ ...p, member_name: e?.target?.value }))} placeholder="Full name" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Email</label><input type="email" value={form?.member_email} onChange={e => setForm(p => ({ ...p, member_email: e?.target?.value }))} placeholder="email@ecity.com" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" /></div>
          <div className="grid grid-cols-2 gap-12">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Role</label><input value={form?.member_role} onChange={e => setForm(p => ({ ...p, member_role: e?.target?.value }))} placeholder="e.g. Technical Consultant" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Department</label><input value={form?.member_department} onChange={e => setForm(p => ({ ...p, member_department: e?.target?.value }))} placeholder="e.g. Technology" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" /></div>
          </div>
          <div className="flex gap-12 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-16 py-10 text-sm font-semibold text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-16 py-10 text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl hover:opacity-90 disabled:opacity-60">
              {saving ? 'Enrolling...' : 'Enroll Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ProgramsSubTab = ({ partners, onEnroll }) => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPartner, setFilterPartner] = useState('');
  const [addModal, setAddModal] = useState(false);
  const [enrollModal, setEnrollModal] = useState(null);
  const { showToast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setPrograms(await trainingProgramsService?.getAll({ status: filterStatus, partnerId: filterPartner || undefined })); }
    catch (e) { showToast?.('Failed to load programs', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStatus, filterPartner]);

  const handleApprove = async (id) => {
    try { await trainingProgramsService?.update(id, { program_status: 'approved' }); load(); showToast?.('Program approved', 'success'); }
    catch (e) { showToast?.('Failed to approve', 'error'); }
  };

  const LEVEL_COLORS = { beginner: 'bg-green-100 text-green-700', intermediate: 'bg-blue-100 text-blue-700', advanced: 'bg-purple-100 text-purple-700' };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-12 mb-20 items-start sm:items-center justify-between">
        <div className="flex gap-12">
          <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            {['all', 'pending', 'approved', 'completed', 'cancelled'].map(s => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
          </select>
          <select value={filterPartner} onChange={e => setFilterPartner(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
            <option value="">All Partners</option>
            {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
          </select>
        </div>
        <button onClick={() => setAddModal(true)} className="flex items-center gap-8 px-16 py-10 text-sm font-semibold text-white bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl hover:opacity-90 transition-opacity">
          <Icon name="Plus" size={15} />Submit Program
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : programs?.length === 0 ? (
        <div className="text-center py-48 text-slate-500"><Icon name="GraduationCap" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">No programs found</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {programs?.map(prog => (
            <div key={prog?.id} className="bg-white rounded-2xl border border-slate-200 p-20 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-12 mb-12">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-8 mb-8">
                    <span className={`px-8 py-3 text-xs font-semibold rounded-full ${STATUS_COLORS?.[prog?.program_status] || 'bg-slate-100 text-slate-600'}`}>{prog?.program_status?.charAt(0)?.toUpperCase() + prog?.program_status?.slice(1)}</span>
                    <span className={`px-8 py-3 text-xs font-semibold rounded-full ${LEVEL_COLORS?.[prog?.level] || 'bg-slate-100 text-slate-600'}`}>{prog?.level}</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm leading-snug">{prog?.program_name}</h3>
                  <p className="text-xs text-slate-500 mt-4">{prog?.partners?.brand_name || prog?.partners?.legal_name || 'Unknown Partner'}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                  <Icon name={FORMAT_ICONS?.[prog?.format] || 'Monitor'} size={18} className="text-violet-600" />
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-14 line-clamp-2">{prog?.description}</p>
              <div className="flex items-center gap-16 text-xs text-slate-500 mb-16">
                <span className="flex items-center gap-6"><Icon name="Clock" size={13} />{prog?.duration || 'N/A'}</span>
                <span className="flex items-center gap-6"><Icon name="Users" size={13} />{prog?.max_seats} seats</span>
                <span className="flex items-center gap-6"><Icon name="Tag" size={13} />{prog?.category}</span>
              </div>
              <div className="flex gap-10">
                {prog?.program_status === 'pending' && (
                  <button onClick={() => handleApprove(prog?.id)} className="flex-1 flex items-center justify-center gap-8 px-14 py-9 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors">
                    <Icon name="CheckCircle" size={14} />Approve
                  </button>
                )}
                {prog?.program_status === 'approved' && (
                  <button onClick={() => setEnrollModal(prog)} className="flex-1 flex items-center justify-center gap-8 px-14 py-9 text-xs font-semibold text-violet-700 bg-violet-50 rounded-xl hover:bg-violet-100 transition-colors">
                    <Icon name="UserPlus" size={14} />Enroll Member
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {addModal && <AddProgramModal partners={partners} onClose={() => setAddModal(false)} onSuccess={() => { setAddModal(false); load(); showToast?.('Program submitted for review', 'success'); }} />}
      {enrollModal && <EnrollModal program={enrollModal} onClose={() => setEnrollModal(null)} onSuccess={() => { setEnrollModal(null); load(); showToast?.('Member enrolled successfully', 'success'); }} />}
    </div>
  );
};

const EnrollmentsSubTab = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const { showToast } = useToast();

  const load = async () => {
    setLoading(true);
    try { setEnrollments(await programEnrollmentsService?.getAll({ status: filterStatus })); }
    catch (e) { showToast?.('Failed to load enrollments', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filterStatus]);

  const handleUpdateStatus = async (id, status) => {
    try { await programEnrollmentsService?.update(id, { attendance_status: status }); load(); showToast?.('Status updated', 'success'); }
    catch (e) { showToast?.('Failed to update', 'error'); }
  };

  return (
    <div>
      <div className="flex gap-12 mb-20">
        <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
          {['all', 'enrolled', 'attended', 'absent'].map(s => <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s?.charAt(0)?.toUpperCase() + s?.slice(1)}</option>)}
        </select>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : enrollments?.length === 0 ? (
        <div className="text-center py-48 text-slate-500"><Icon name="Users" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">No enrollments found</p></div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{['Member', 'Role / Dept', 'Program', 'Enrolled', 'Score', 'Status', 'Actions'].map(h => <th key={h} className="px-16 py-12 text-left text-xs font-semibold text-slate-600">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {enrollments?.map(e => (
                <tr key={e?.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-16 py-12">
                    <p className="font-medium text-slate-900">{e?.member_name}</p>
                    <p className="text-xs text-slate-500">{e?.member_email}</p>
                  </td>
                  <td className="px-16 py-12 text-xs text-slate-500">{e?.member_role}<br />{e?.member_department}</td>
                  <td className="px-16 py-12 text-slate-700 text-xs">{e?.training_programs?.program_name || '—'}</td>
                  <td className="px-16 py-12 text-slate-500 text-xs">{e?.enrollment_date ? new Date(e?.enrollment_date)?.toLocaleDateString() : '—'}</td>
                  <td className="px-16 py-12 text-slate-700 font-medium">{e?.score != null ? `${e?.score}%` : '—'}</td>
                  <td className="px-16 py-12">
                    <span className={`px-10 py-4 text-xs font-semibold rounded-full ${STATUS_COLORS?.[e?.attendance_status] || 'bg-slate-100 text-slate-600'}`}>{e?.attendance_status?.charAt(0)?.toUpperCase() + e?.attendance_status?.slice(1)}</span>
                  </td>
                  <td className="px-16 py-12">
                    {e?.attendance_status === 'enrolled' && (
                      <button onClick={() => handleUpdateStatus(e?.id, 'attended')} className="px-10 py-6 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">Mark Attended</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MaterialsSubTab = ({ partners }) => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => { trainingProgramsService?.getAll({ status: 'approved' })?.then(setPrograms)?.catch(() => {}); }, []);

  useEffect(() => {
    if (!selectedProgram) { setMaterials([]); return; }
    setLoading(true);
    programMaterialsService?.getByProgram(selectedProgram)?.then(setMaterials)?.catch(() => showToast?.('Failed to load materials', 'error'))?.finally(() => setLoading(false));
  }, [selectedProgram]);

  const MATERIAL_ICONS = { document: 'FileText', video: 'Video', slide: 'Presentation', guide: 'BookOpen' };

  return (
    <div>
      <div className="mb-20">
        <select value={selectedProgram} onChange={e => setSelectedProgram(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
          <option value="">Select a program to view materials...</option>
          {programs?.map(p => <option key={p?.id} value={p?.id}>{p?.program_name}</option>)}
        </select>
      </div>
      {!selectedProgram ? (
        <div className="text-center py-48 text-slate-400"><Icon name="FileText" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">Select a program to view its materials</p></div>
      ) : loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : materials?.length === 0 ? (
        <div className="text-center py-48 text-slate-500"><Icon name="FileText" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">No materials uploaded for this program</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-16">
          {materials?.map(m => (
            <div key={m?.id} className="bg-white rounded-2xl border border-slate-200 p-20 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-12">
                <Icon name={MATERIAL_ICONS?.[m?.material_type] || 'File'} size={18} className="text-violet-600" />
              </div>
              <h3 className="font-semibold text-slate-900 text-sm mb-4">{m?.material_name}</h3>
              <p className="text-xs text-slate-500 mb-12">{m?.description || m?.material_type?.charAt(0)?.toUpperCase() + m?.material_type?.slice(1)}</p>
              <div className="flex items-center justify-between text-xs text-slate-400">
                <span>{m?.file_size || 'N/A'}</span>
                <span className={`px-8 py-3 rounded-full ${m?.access_level === 'enrolled' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{m?.access_level}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const FeedbackSubTab = ({ partners }) => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [feedback, setFeedback] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  useEffect(() => { trainingProgramsService?.getAll({ status: 'approved' })?.then(setPrograms)?.catch(() => {}); }, []);

  useEffect(() => {
    if (!selectedProgram) { setFeedback([]); setAvgRating(0); return; }
    setLoading(true);
    Promise.all([
      programFeedbackService?.getByProgram(selectedProgram),
      programFeedbackService?.getAverageRating(selectedProgram)
    ])?.then(([fb, avg]) => { setFeedback(fb); setAvgRating(avg); })
      ?.catch(() => showToast?.('Failed to load feedback', 'error'))
      ?.finally(() => setLoading(false));
  }, [selectedProgram]);

  const renderStars = (rating) => Array.from({ length: 5 }, (_, i) => (
    <Icon key={i} name="Star" size={14} className={i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
  ));

  return (
    <div>
      <div className="mb-20">
        <select value={selectedProgram} onChange={e => setSelectedProgram(e?.target?.value)} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none bg-white">
          <option value="">Select a program to view feedback...</option>
          {programs?.map(p => <option key={p?.id} value={p?.id}>{p?.program_name}</option>)}
        </select>
      </div>
      {selectedProgram && !loading && feedback?.length > 0 && (
        <div className="flex items-center gap-16 p-16 bg-amber-50 rounded-2xl border border-amber-100 mb-20">
          <div className="text-3xl font-bold text-amber-600">{avgRating}</div>
          <div>
            <div className="flex gap-4 mb-4">{renderStars(Math.round(avgRating))}</div>
            <p className="text-xs text-slate-600">{feedback?.length} review{feedback?.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      )}
      {!selectedProgram ? (
        <div className="text-center py-48 text-slate-400"><Icon name="Star" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">Select a program to view feedback</p></div>
      ) : loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : feedback?.length === 0 ? (
        <div className="text-center py-48 text-slate-500"><Icon name="Star" size={40} className="mx-auto mb-12 opacity-30" /><p className="font-medium">No feedback yet for this program</p></div>
      ) : (
        <div className="space-y-14">
          {feedback?.map(fb => (
            <div key={fb?.id} className="bg-white rounded-2xl border border-slate-200 p-20">
              <div className="flex items-start justify-between gap-12 mb-10">
                <div>
                  <p className="font-semibold text-slate-900 text-sm">{fb?.member_name || 'Anonymous'}</p>
                  <p className="text-xs text-slate-500">{fb?.submitted_at ? new Date(fb?.submitted_at)?.toLocaleDateString() : '—'}</p>
                </div>
                <div className="flex gap-3">{renderStars(fb?.rating || 0)}</div>
              </div>
              {fb?.comments && <p className="text-sm text-slate-700 mb-8">{fb?.comments}</p>}
              {fb?.improvement_suggestions && <p className="text-xs text-slate-500 italic">Suggestion: {fb?.improvement_suggestions}</p>}
              {fb?.would_recommend !== null && (
                <div className="mt-10 flex items-center gap-6 text-xs">
                  <Icon name={fb?.would_recommend ? 'ThumbsUp' : 'ThumbsDown'} size={13} className={fb?.would_recommend ? 'text-emerald-500' : 'text-red-500'} />
                  <span className={fb?.would_recommend ? 'text-emerald-600' : 'text-red-600'}>{fb?.would_recommend ? 'Would recommend' : 'Would not recommend'}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PartnerToeCityTraining = () => {
  const [subTab, setSubTab] = useState('programs');
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    partnerService?.getAll?.()?.then(setPartners)?.catch(() => {});
  }, []);

  return (
    <div>
      <div className="mb-20 p-16 bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl border border-violet-100">
        <div className="flex items-center gap-12 mb-6">
          <Icon name="ArrowLeft" size={18} className="text-violet-600" />
          <h2 className="text-base font-bold text-slate-900">Partner → eCity Training</h2>
        </div>
        <p className="text-sm text-slate-600">Partners upskill eCity staff on their products, technologies, and solutions.</p>
      </div>

      <div className="flex gap-8 mb-24 overflow-x-auto pb-2">
        {SUB_TABS?.map(tab => (
          <button key={tab?.id} onClick={() => setSubTab(tab?.id)}
            className={`flex items-center gap-8 px-16 py-10 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${subTab === tab?.id ? 'bg-violet-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}>
            <Icon name={tab?.icon} size={15} />{tab?.label}
          </button>
        ))}
      </div>

      {subTab === 'programs' && <ProgramsSubTab partners={partners} onEnroll={() => {}} />}
      {subTab === 'enrollments' && <EnrollmentsSubTab />}
      {subTab === 'materials' && <MaterialsSubTab partners={partners} />}
      {subTab === 'feedback' && <FeedbackSubTab partners={partners} />}
    </div>
  );
};

export default PartnerToeCityTraining;
